// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company location assets page component.
 * Displays assets that are located at the company's locations.
 */
import { AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import {
  AssetSearchListing,
  assetSearchListingSchema,
} from '@/asset/hooks/use-asset-filters';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { getCustomer } from '@/customer/api/get-customer';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { useSort } from '@/hooks/use-sort';
import { Stack, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

const ASSET_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'product',
  'owner',
  'activity',
];

export const Route = createFileRoute(
  '/_app/companies/$companyKey/location-assets'
)({
  component: CompanyLocationAssetsRoute,
  validateSearch: assetSearchListingSchema,
});

function CompanyLocationAssetsRoute() {
  const { companyKey } = Route.useParams();
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleChangePage = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  return (
    <CustomerErrorBoundary>
      <CompanyLocationAssetsContent
        companyKey={companyKey}
        assetSearchParams={searchParameters}
        handlePageChange={handleChangePage}
      />
    </CustomerErrorBoundary>
  );
}

interface CompanyLocationAssetsContentProps {
  companyKey: string;
  assetSearchParams?: AssetSearchListing;
  handlePageChange?: (page: number) => void;
}

function CompanyLocationAssetsContent(
  props: CompanyLocationAssetsContentProps
) {
  const locationTableSort = useSort<AssetSortProperty>('serial');

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  return (
    <HardfinTabCardWrapper title="Assets on location">
      <Stack paddingTop={2}>
        <Suspense
          fallback={<AssetTableSkeleton columns={ASSET_TABLE_COLUMNS.length} />}
        >
          <AssetTable
            sort={locationTableSort.sort}
            columns={ASSET_TABLE_COLUMNS}
            onSort={locationTableSort.setSort}
            filter={{
              customerLocations: [data.customer.id],
              pageSize: 50,
              pageNumber: props.assetSearchParams?.page ?? 1,
            }}
            onPageChange={props.handlePageChange}
            emptyState={
              <Typography variant="body2" color="text.secondary">
                There are no assets at this company's locations
              </Typography>
            }
          />
        </Suspense>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
